import React from "react";
import { FiMoreVertical } from "react-icons/fi";
import { Menu, MenuItem } from "@mui/material";

import {
  TableContainer,
  StyledTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  ActionButton,
  ActionsWrapper,
  ActionsDropdownMenu,
  ActionOption
} from "./styles";

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  width?: string;
}

export interface TableAction<T> {
  label: string;
  icon?: React.ReactNode;
  onClick: (row: T, event?: React.MouseEvent) => void;
  hidden?: (row: T) => boolean;
  disabled?: (row: T) => boolean;
  position?: "menu" | "outside";
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  actions?: TableAction<T>[];
  rowClassName?: (row: T) => string;
}

export function Table<T>({ columns, data, onRowClick, actions, rowClassName }: TableProps<T>) {
  return (
    <TableContainer>
      <StyledTable>
        <Thead>
          <Tr>
            {columns.map((col) => (
              <Th key={col.key} colWidth={col.width}>{col.header}</Th>
            ))}
            {actions && actions.length > 0 && <Th className="actions-header"></Th>}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, rowIndex) => (
            <Tr
              key={rowIndex}
              className={rowClassName ? rowClassName(row) : undefined}
              clickable={!!onRowClick}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {columns.map((col) => (
                <Td key={col.key} data-label={col.header} colWidth={col.width}>
                  {col.render ? col.render(row) : (row as any)[col.key]}
                </Td>
              ))}
              {actions && actions.length > 0 && (
                <Td className="actions-cell">
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
                    {actions
                      .filter(a => a.position === "outside" && !(a.hidden && a.hidden(row)))
                      .map((action, idx) => {
                        const isDisabled = action.disabled && action.disabled(row);
                        return (
                          <ActionButton
                            key={`outside-${idx}`}
                            disabled={isDisabled}
                            style={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? "not-allowed" : "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isDisabled) action.onClick(row, e);
                            }}
                            title={action.label}
                          >
                            {action.icon}
                          </ActionButton>
                        );
                      })}
                    {actions.filter(a => a.position !== "outside" && !(a.hidden && a.hidden(row))).length > 0 && (
                      <ActionsMenu row={row} actions={actions.filter(a => a.position !== "outside" && !(a.hidden && a.hidden(row)))} />
                    )}
                  </div>
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </StyledTable>
    </TableContainer>
  );
}

function ActionsMenu<T>({ row, actions }: { row: T; actions: TableAction<T>[] }) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <ActionButton isOpen={open} onClick={handleClick}>
        <FiMoreVertical size={20} />
      </ActionButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
            border: "1px solid rgba(0, 0, 0, 0.05)",
            width: "210px",
            padding: "4px 0",
          }
        }}
      >
        {actions.map((action, idx) => {
          if (action.hidden && action.hidden(row)) return null;

          const isDisabled = action.disabled && action.disabled(row);

          return (
            <MenuItem
              key={idx}
              disabled={isDisabled}
              onClick={(e) => {
                e.stopPropagation();
                if (isDisabled) return;
                setAnchorEl(null);
                action.onClick(row, e);
              }}
              sx={{
                width: "100%",
                height: "40px",
                padding: "4px 16px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? "not-allowed" : "pointer",
                backgroundColor: "var(--white)",
                "&:hover": {
                  backgroundColor: isDisabled ? "var(--white)" : "#f5f5f5",
                },
                svg: {
                  width: "18px",
                  height: "18px",
                  color: "var(--primary)",
                },
                span: {
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "var(--primary)",
                }
              }}
            >
              {action.icon}
              <span>{action.label}</span>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
